import type { ComponentType } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { ListParamsProvider } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import { getOpportunityListParams } from "app/pages/sales-intelligence/pages/opportunity-list/hoc/utils";
import type { CrmListRoutingParamsWithId } from "app/pages/sales-intelligence/sub-modules/opportunities/types/routing";
import type { UnknownProps } from "app/pages/sales-intelligence/types";

/** Derives the `opportunity-list` params from the route at the page boundary — the single place
    guaranteed to be on a list route — and exposes them to the subtree via context. Wrap this
    OUTSIDE {@link withNotFoundListGuard} so the guard (and everything below) reads the non-nullable
    context value instead of re-deriving from the global route. Route derivation lives here and
    ONLY here on purpose: subtree code must consume {@link useListParams}, never the router. */
function withListParamsProvider<P extends UnknownProps>(ListPageComponent: ComponentType<P>): ComponentType<P> {
    return function WrappedWithListParamsProvider(props: P) {
        const { params, state } = useCurrentStateAndParams<CrmListRoutingParamsWithId>();

        const listParams = useMemo(() => getOpportunityListParams(params, state), [params, state]);

        if (!listParams) {
            return null;
        }

        return (
            <ListParamsProvider value={listParams}>
                <ListPageComponent {...props} />
            </ListParamsProvider>
        );
    };
}

export default withListParamsProvider;
